import clsx from 'clsx'
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react'
import { useEffect } from 'react'

export type Tamanho =
  | 'mini'
  | 'pequeno'
  | 'medio'
  | 'grande'
  | 'gigante'
  | 'largura-total'

interface DialogoRootProps extends HTMLAttributes<HTMLDivElement> {
  tamanho?: Tamanho
  deveSerExibido?: boolean
  overlay?: ReactNode
  aoFechar?: () => void
}

function DialogoRoot({
  tamanho = 'medio',
  deveSerExibido,
  overlay,
  aoFechar,
  className,
  children,
  ...rest
}: DialogoRootProps): JSX.Element | null {
  useEffect(() => {
    if (!deveSerExibido || !aoFechar) return undefined

    function tratarTeclaPressionada(evento: KeyboardEvent): void {
      if (evento.key === 'Escape') {
        evento.stopPropagation()
        aoFechar?.()
      }
    }

    document.addEventListener('keydown', tratarTeclaPressionada)

    return () => {
      document.removeEventListener('keydown', tratarTeclaPressionada)
    }
  }, [deveSerExibido, aoFechar])

  if (!deveSerExibido) return null

  return (
    <div
      {...rest}
      role="dialog"
      aria-modal="true"
      className={clsx(
        'dialogo',
        `dialogo_${tamanho}`,
        {
          dialogo_ativo: deveSerExibido,
        },
        className
      )}
    >
      {overlay}

      <div className="dialogo__container">{children}</div>
    </div>
  )
}

function DialogoHeader({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div {...rest} className={clsx('dialogo__cabecalho', className)}>
      {children}
    </div>
  )
}

function DialogoTitle({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLHeadingElement>): JSX.Element {
  return (
    <h2 {...rest} className={clsx('dialogo__titulo', className)}>
      {children}
    </h2>
  )
}

function DialogoBody({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div {...rest} className={clsx('dialogo__corpo', className)}>
      {children}
    </div>
  )
}

function DialogoFooter({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div {...rest} className={clsx('dialogo__rodape', className)}>
      {children}
    </div>
  )
}

type DialogoCloseButtonProps = ButtonHTMLAttributes<HTMLButtonElement>

function DialogoCloseButton({
  className,
  'aria-label': ariaLabel,
  children,
  ...rest
}: DialogoCloseButtonProps): JSX.Element {
  return (
    <button
      {...rest}
      type="button"
      className={clsx('dialogo__botao-fechar', className)}
      aria-label={ariaLabel ?? 'Fechar diálogo'}
    >
      {children ?? <i className="far fa-times" />}
    </button>
  )
}

export const Dialogo = {
  Root: DialogoRoot,
  Header: DialogoHeader,
  Title: DialogoTitle,
  Body: DialogoBody,
  Footer: DialogoFooter,
  CloseButton: DialogoCloseButton,
}

export type DialogoComponent = typeof Dialogo

export default Dialogo
